<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid create-job-container p-b-40">
        <v-form v-model="isFormValid">
          <v-card class="p-b-30 p-t-30 p-l-20">
            <div class="el-loading-mask" v-if="loading">
              <div class="el-loading-spinner">
                <svg viewBox="25 25 50 50" class="circular">
                  <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
                </svg>
              </div>
            </div>
            <!-- Card Title  -->
            <div class="row">
              <h3 class="title-1 p-l-185">
                {{ text('create_wf_btn') }}
              </h3>
            </div>
            <!-- Name and Profile -->
            <div class="section">
              <div class="row p-b-10">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 section-description">
                  {{ text('wf_name_text') }}
                </div>
              </div>
              <div class="row">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 p-r-0">
                  <v-text-field
                    v-model="form.name"
                    id="name"
                    :label="text('wf_name_label')"
                    :counter="255"
                    :rules="rules.nameRules"
                    required>
                  </v-text-field>
                  <v-select
                    :disabled="isEditing"
                    prepend-icon="fas fa-sliders-h"
                    v-model="form.profile"
                    :label="text('encoding_profile_label')"
                    item-text="name"
                    item-value="id"
                    :append-icon="form.profile ? 'zoom_in' : ''"
                    @click:append="profileInfoDialog=true"
                    :items="profiles">
                  </v-select>
                </div>
              </div>
            </div>
            <!-- Folder -->
            <div class="section">
              <div class="row p-b-10">
                <div class="col-md-2 col-l">
                  <h2>{{ text('input') }}</h2>
                </div>
                <div class="col-md-10 section-description">
                  {{ text('wf_input_text') }}
                </div>
              </div>
              <div class="row">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 p-r-0">
                  <v-text-field
                    outline
                    hide-details
                    v-model="form.url.url"
                    @change="onInputURLChange"
                    label="Folder*"
                    :counter="1024"
                    :placeholder="form.url.selectedType + '://host/path'"
                    :append-icon="form.url.showDetails ? 'visibility_off' : 'visibility'"
                    @click:append="form.url.showDetails = !form.url.showDetails"
                    :error="form.url.url !== '' && !form.url.hostPath">
                  </v-text-field>
                  <v-expand-transition>
                    <div class="url-details p-l-10" v-show="form.url.showDetails">
                      <div class="row p-t-0">
                        <div class="col-md-5">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.url.login"
                            label="Login">
                          </v-text-field>
                        </div>
                        <div class="col-md-7">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.url.password"
                            :label="form.url.selectedType === 'azure' ? 'Shared key' : 'Password'">
                          </v-text-field>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-md-12">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.url.hostPath"
                            label="Host/Path">
                          </v-text-field>
                        </div>
                      </div>
                      <div class="row p-t-20 p-b-10">
                        <div class="col-md-12 secure-setting">
                          <v-checkbox
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.url.secure">
                          </v-checkbox>
                          <span
                            @click="changeInputSecure"
                            class="custom-selector-label p-l-20">
                            Secure
                          </span>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                  <div class="selection-control m-t-20">
                    <v-checkbox
                      hide-details
                      v-model="form.once"/>
                    <span
                      class="custom-selector-label p-l-15"
                      @click="form.once = !form.once">
                      {{ text('perform_once') }}
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon color="grey" v-on="on" size="20">
                            fa-question-circle
                          </v-icon>
                        </template>
                        <span>In this case, the specified directory is scanned once immediately
                            after the watch folder is created.
                        </span>
                      </v-tooltip>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- OUTPUTS -->
            <div class="section">
              <div class="row p-b-5">
                <div class="col-md-2 col-l">
                  <h2>{{ text('output_header') }}</h2>
                </div>
                <div class="col-md-10 section-description">
                  <!--Add one or more output <span @click="formatInfoDialog=true">formats</span> and
                  specify the path(s) to the server folder to upload the result to.-->
                  {{ text('wf_output_text') }}
                </div>
              </div>
              <div class="row" v-for="(output, i) of form.outputs"
                   :key="i"
                   :class="{'p-t-20': i > 0, 'p-b-20': form.outputs.length > 1 && i < form.outputs.length-1}">
                <div class="col-md-2 col-l">
                  <p class="p-t-15">
                    #{{ i+1 }} {{ text('of') }} {{ form.outputs.length }}
                  </p>
                  <p v-if="form.outputs.length > 1">
                    <i class="zmdi zmdi-minus remove-icon p-r-5"></i>
                    <a class="remove-output" @click="removeOutput(i)">{{ text('remove_btn') }}</a>
                  </p>
                </div>
                <div class="col-md-10 p-r-0">
                  <v-tabs style="min-height: 210px;">
                    <v-tab>{{ text('general_tab') }}</v-tab>
                    <v-tab>DRM</v-tab>
                    <v-tab>{{ text('compress_tab') }}</v-tab>
                    <!-- General tab -->
                    <v-tab-item>
                      <div class="row p-t-20">
                        <div class="col-md-4" style="max-width: 200px;">
                          <v-select
                            v-model="output.format"
                            :label="text('format_label')"
                            item-text="title"
                            item-value="extId"
                            @change="onFormatChange(i)"
                            :items="formats">
                          </v-select>
                        </div>
                        <div class="col-md-4"
                             style="max-width: 200px;"
                             v-if="output.format === 'segmented_hls'">
                          <v-select
                            :items="hlsVersions"
                            :label="text('version_label')"
                            v-model="output.hlsVersion"></v-select>
                        </div>
                        <div class="col-md-4"
                             style="max-width: 200px;"
                             v-if="['segmented_hls', 'segmented_dash', 'segmented_ss'].includes(output.format)">
                          <v-text-field
                            type="number"
                            :label="text('segment_size_label')"
                            v-model="output.segmentSize"
                            :rules="rules.segmentSize"
                            :suffix="text('sec')"></v-text-field>
                        </div>
                      </div>
                      <div class="row p-t-10">
                        <div class="col-md-12 p-r-0" v-for="(url, j) of output.urls" :key="j">
                          <v-text-field
                            outline
                            v-model="url.url"
                            @change="onOutputURLChange(i, j)"
                            :placeholder="url.selectedType + '://host/path'"
                            :label="outputLabel(i, j)"
                            :counter="1024"
                            :append-icon="url.showDetails ? 'visibility_off' : 'visibility'"
                            @click:append="url.showDetails = !url.showDetails"
                            :error="url.url !== '' && !url.hostPath && url.selectedType !== 'origin'"
                            @click:append-outer="addOutputURL(i)">
                            <template v-slot:append-outer>
                              <v-btn
                                text icon
                                class="m-0"
                                @click.stop="removeOutputURL(i, j)"
                                :disabled="output.urls.length === 1">
                                <i class="zmdi zmdi-delete"></i>
                              </v-btn>
                              <v-btn
                                text icon
                                class="m-0"
                                @click.stop="addOutputURL(i)"
                                :disabled="j < output.urls.length-1">
                                <i class="zmdi zmdi-plus"></i>
                              </v-btn>
                            </template>
                          </v-text-field>
                          <v-expand-transition>
                            <div class="url-details p-l-10" v-show="url.showDetails">
                              <v-radio-group v-model="url.selectedType" row height="3" hide-details>
                                <v-radio :value="'ftp'" @change="changeOutputType(i, j, 'ftp')"></v-radio>
                                <div class="custom-selector-label" @click="changeOutputType(i, j, 'ftp')">FTP</div>
                                <v-radio :value="'aspera'" @change="changeOutputType(i, j, 'aspera')"></v-radio>
                                <div class="custom-selector-label" @click="changeOutputType(i, j, 'aspera')">Aspera</div>
                                <v-radio :value="'azure'" @change="changeOutputType(i, j, 'azure')"></v-radio>
                                <div class="custom-selector-label" @click="changeOutputType(i, j, 'azure')">Azure</div>
                                <v-radio :value="'origin'" @change="changeOutputType(i, j, 'origin')"></v-radio>
                                <div class="custom-selector-label" @click="changeOutputType(i, j, 'origin')">Origin</div>
                              </v-radio-group>
                              <div class="row p-t-10">
                                <div class="col-md-5">
                                  <v-text-field
                                    hide-details
                                    @change="buildOutputUrl(i, j)"
                                    v-model="url.login"
                                    label="Login"></v-text-field>
                                </div>
                                <div class="col-md-7 p-r-95">
                                  <v-text-field
                                    hide-details
                                    @change="buildOutputUrl(i, j)"
                                    v-model="url.password"
                                    :label="url.selectedType === 'azure' ? 'Shared key' : 'Password'">
                                  </v-text-field>
                                </div>
                              </div>
                              <div class="row p-b-10"
                                   :class="{'p-b-30': output.urls.length > 1 && j < output.urls.length-1 && url.selectedType !== 'ftp'}">
                                <div class="col-md-12 p-r-95">
                                  <v-text-field
                                    hide-details
                                    @change="buildOutputUrl(i, j)"
                                    v-model="url.hostPath"
                                    label="Host/Path"></v-text-field>
                                </div>
                              </div>
                              <div class="row p-b-20"
                                   v-show="['http', 'ftp'].includes(url.selectedType)"
                                   :class="{'p-b-30': output.urls.length > 1 && j < output.urls.length-1}">
                                <div class="col-md-12 secure-setting">
                                  <v-checkbox
                                    hide-details
                                    @change="buildOutputUrl(i, j)"
                                    v-model="url.secure"></v-checkbox>
                                  <span @click="changeOutputSecure(i, j)"
                                        class="custom-selector-label p-l-20">Secure</span>
                                </div>
                              </div>
                            </div>
                          </v-expand-transition>
                        </div>
                      </div>
                    </v-tab-item>
                    <!-- DRM tab -->
                    <v-tab-item>
                      <div class="row p-t-20">
                        <div class="col-md-12 p-r-15">
                          <p class="tab-description p-b-10">
                            The Widevine DRM protection is only supported for the
                            <strong>MPEG-DASH</strong> format.<br/>
                            The PlayReady DRM is for the <strong>MPEG-DASH</strong>, and
                            <strong>MSS</strong> formats.
                          </p>
                          <v-radio-group v-model="output.drm" row class="p-0" height="3" hide-details>
                            <v-radio
                              :value="'none'"
                              @change="setDRM(i, 'none')"/>
                            <div class="custom-selector-label" @click="setDRM(i, 'none')">
                              none
                            </div>
                            <template v-if="output.format === 'segmented_dash'">
                              <v-radio :value="'widevine'" @change="setDRM(i, 'widevine')"/>
                              <div class="custom-selector-label" @click="setDRM(i, 'widevine')">
                                Widevine
                              </div>
                            </template>
                            <template v-if="['segmented_dash', 'segmented_ss'].includes(output.format)">
                              <v-radio :value="'playready'" @change="setDRM(i, 'playready')"/>
                              <div class="custom-selector-label" @click="setDRM(i, 'playready')">
                                PlayReady
                              </div>
                            </template>
                          </v-radio-group>
                          <!-- Widevine Fields -->
                          <v-expand-transition>
                            <div v-show="output.drm === 'widevine'" class="p-t-20">
                              <div class="row">
                                <div class="col-md-12">
                                  <v-text-field
                                    v-model.trim="output.widevine.licenseServer"
                                    :counter="1024"
                                    :rules="rules.drmServerURL"
                                    label="*Key Server URL"
                                    hint="e.g. https://my-license-server/cenc/"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-12">
                                  <v-text-field
                                    v-model.trim="output.widevine.provider"
                                    :counter="512"
                                    :rules="rules.wvProvider"
                                    hint="e.g. my-provider"
                                    label="*Provider"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-7">
                                  <v-text-field
                                    v-model="output.widevine.key"
                                    :counter="64"
                                    :rules="rules.wvKey"
                                    label="*AES 128-bit signing key (64 characters)"
                                    clearable></v-text-field>
                                </div>
                                <div class="col-md-5 p-l-20">
                                  <v-text-field
                                    v-model="output.widevine.iv"
                                    :counter="32"
                                    :rules="rules.wvIV"
                                    label="*AES signing IV (32 characters)"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-12">
                                  <v-text-field
                                    v-model="output.widevine.packageName"
                                    :counter="128"
                                    :rules="rules.wvPackageName"
                                    hint="e.g. output_1080p"
                                    label="*Package Name"
                                    clearable></v-text-field>
                                </div>
                              </div>
                            </div>
                          </v-expand-transition>
                          <!-- PlayReady Fields -->
                          <v-expand-transition>
                            <div v-show="output.drm === 'playready'" class="p-t-20">
                              <div class="row">
                                <div class="col-md-12">
                                  <v-text-field
                                    v-model.trim="output.playready.licenseServerURL"
                                    :counter="1024"
                                    :rules="rules.drmServerURL"
                                    label="*Key Server URL"
                                    hint="e.g. https://test.playready.microsoft.com/service/rightsmanager.asmx"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-5 p-r-20">
                                  <v-text-field
                                    v-model.trim="output.playready.kidUUID"
                                    :counter="36"
                                    :rules="rules.prKeyID"
                                    label="*UUID representation of Key ID (36 characters)"
                                    hint="e.g. 10000000-1000-1000-1000-100000000001"
                                    clearable></v-text-field>
                                </div>
                                <div class="col-md-7">
                                  <v-text-field
                                    v-model.trim="output.playready.keySeed"
                                    :counter="512"
                                    :rules="rules.prKeySeed"
                                    label="*KeySeed"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-5 p-r-20">
                                  <v-text-field
                                    v-model.trim="output.playready.cekB64"
                                    :counter="24"
                                    :rules="rules.prCekB64"
                                    label="*Base64 of Content Encryption Key (24 characters)"
                                    clearable></v-text-field>
                                </div>
                              </div>
                            </div>
                          </v-expand-transition>
                        </div>
                      </div>
                    </v-tab-item>
                    <!-- Compress tab -->
                    <v-tab-item>
                      <div class="row p-t-20">
                        <div class="col-md-12 p-r-15">
                          <p class="tab-description p-b-10">
                            If you need to compress the result into an archive,
                            specify the archive's name below.<br/>
                            The archives will be uploaded to all destination URLs specified in the
                            output.
                          </p>
                          <v-text-field
                            label="Archive name"
                            v-model.trim="output.zip"
                            :placeholder="'Output_' + (i+1) + '.zip'"
                            clearable></v-text-field>
                        </div>
                      </div>
                    </v-tab-item>
                  </v-tabs>
                </div>
              </div>
            </div>
            <!-- Add output button -->
            <div class="row p-l-45 add-output">
              <i class="zmdi zmdi-plus p-r-5"></i>
              <a @click="addOutput()">{{ text('add_output_btn') }}</a>
            </div>
            <!-- Action button -->
            <div class="row text-right p-t-10">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10">
                <v-btn
                  large
                  :loading="isSaving"
                  :disabled="isSaving || !isWFValid"
                  color="success"
                  @click="saveWF">
                  <span>Create</span>
                </v-btn>
              </div>
            </div>
            <!-- Format Info Dialog -->
            <v-dialog v-model="formatInfoDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
              <v-card>
                <v-toolbar dark color="primary">
                  <v-btn icon dark @click="formatInfoDialog = false">
                    <v-icon>zmdi-close</v-icon>
                  </v-btn>
                  <v-toolbar-title>Video file format description</v-toolbar-title>
                </v-toolbar>
                <table class="format-description">
                  <tr v-for="(format) in formats" class="p-3" :key="format.title">
                    <td>
                      <img :alt='format.title' class="m-2" :src="'/images/video_formats/' + format.extId + '.png'" style="width: 150px;">
                    </td>
                    <td>
                      <h4 class="p-b-10">{{ format.title }}</h4>
                      <p v-html="format.description"></p>
                    </td>
                  </tr>
                </table>
              </v-card>
            </v-dialog>
          </v-card>
        </v-form>
      </div>
    </div>

    <!-- Profile Info Dialog -->
    <v-dialog v-model="profileInfoDialog" max-width="900">
      <v-card class="p-b-15">
        <v-card-text v-if="selectedProfile.name">
          <div class="row">
            <div class="col-md-12">
              <v-btn
                icon
                @click="profileInfoDialog = false">
                <v-icon>zmdi-close</v-icon>
              </v-btn>
              <span class="headline align-middle">{{ selectedProfile.name }}</span>
            </div>
          </div>
          <v-divider/>
          <table class="profile-detail-table">
            <tbody>
            <tr>
              <td><span class="card-item-title">ID</span></td>
              <td>{{ selectedProfile.id }}</td>
            </tr>
            <tr>
              <td><span class="card-item-title">Audio codec</span></td>
              <td class="text-uppercase">{{ selectedProfile.audio_template.codec }}</td>
            </tr>
            <tr>
              <td><span class="card-item-title">Video codec</span></td>
              <td>
                      <span class="text-uppercase" v-if="selectedProfile.video_template.codec">
                        {{ videoCodecMap[selectedProfile.video_template.codec][0] }}
                      </span>
              </td>
            </tr>
            <tr>
              <td><span class="card-item-title">Video streams</span></td>
              <td>{{ selectedProfile.video_template.streams.length }}</td>
            </tr>
            <tr v-if="selectedProfile.video_template.codec.startsWith('h26')">
              <td><span class="card-item-title">Encoder</span></td>
              <td>
                      <span v-if="selectedProfile.video_template.codec">
                        {{ videoCodecMap[selectedProfile.video_template.codec][1]
                        || videoCodecMap[selectedProfile.video_template.codec][2] }}
                      </span>
              </td>
            </tr>
            </tbody>
          </table>
          <v-divider/>
          <table class="profile-detail-table">
            <tbody>
            <tr>
              <td><span class="card-item-title">Audio channels</span></td>
              <td>
                {{ audioChannelsMap[selectedProfile.audio_template.channels]
                || 'Same as source' }}
              </td>
            </tr>
            <tr>
              <td><span class="card-item-title">Audio bitrate</span></td>
              <td>{{ selectedProfile.audio_template.bitrate }} Kbps</td>
            </tr>
            <tr>
              <td><span class="card-item-title">Sample rate</span></td>
              <td>
                      <span v-if="selectedProfile.audio_template.sample_rate">
                        {{ selectedProfile.audio_template.sample_rate }} Hz
                      </span>
                <span v-else>Same as source</span>
              </td>
            </tr>
            </tbody>
          </table>
          <template v-if="selectedProfile.video_template.streams.length">
            <table class="profile-streams m-t-15">
              <thead>
              <tr>
                <th class="number-column">Video stream</th>
                <th v-if="selectedProfile.video_template.codec.startsWith('h26')">
                  Profile
                </th>
                <th v-if="selectedProfile.video_template.codec.startsWith('h26')">Level</th>
                <th>Resolution</th>
                <th>Bitrate, Kbps</th>
                <th>Resize Mode</th>
                <th>Frame Rate, Fps</th>
                <th>KeyInt / MinKeyInt</th>
                <th v-if="selectedProfile.video_template.codec.startsWith('h26')">
                  Ref Frames / BFrames
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(stream, i) of selectedProfile.video_template.streams" :key="i">
                <td>#{{ i+1 }}</td>
                <td v-if="selectedProfile.video_template.codec.startsWith('h264')">
                  {{ h264ProfileMap[stream.h264_codec_profile] }}
                </td>
                <td v-if="selectedProfile.video_template.codec.startsWith('h265')">
                  {{ h265ProfileMap[stream.h264_codec_profile] }}
                </td>
                <td v-if="selectedProfile.video_template.codec.startsWith('h264')">
                  {{ stream.h264_codec_level || 'Auto' }}
                </td>
                <td v-if="selectedProfile.video_template.codec.startsWith('h265')">
                  {{ stream.h264_codec_level || 'Auto' }}
                </td>
                <td>{{ stream.resolution || 'Same as source'}}</td>
                <td>{{ stream.bitrate }}</td>
                <td>{{ resizeModeMap[stream.aspect_ratio] || 'Same as source' }}</td>
                <td>
                  <span v-if="stream.target_fps">{{ stream.target_fps }}</span>
                  <span v-else>Same as source</span>
                <td>{{ stream.keyframe_int || '0' }}/{{ stream.min_keyframe_int || '0' }}</td>
                <td v-if="selectedProfile.video_template.codec.startsWith('h26')">
                  {{ stream.h264_reference_frames || '0' }}/{{ stream.h264_bframes || '0' }}
                </td>
              </tr>
              </tbody>
            </table>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- -->
  </div>
</template>

<script>
import Vue from 'vue';
import {
  AUDIO_CHANNELS_MAP,
  H264_CODEC_PROFILE_MAP,
  H265_CODEC_PROFILE_MAP,
  ENCODING_FORMATS,
  RESIZE_MODE_MAP,
  VIDEO_CODEC_MAP,
} from '@/components/transcoding/shared/types';
import { processURLInfo } from '@/components/transcoding/shared/url';

const outputObject = {
  format: 'segmented_hls',
  drm: 'none',
  urls: [], // array of urlObject
  zip: '',
  segmentSize: 4,
  hlsVersion: 3,
  widevine: {
    licenseServer: '',
    provider: '',
    key: '',
    iv: '',
    packageName: '',
  },
  playready: {
    licenseServerURL: '',
    kidUUID: '',
    keySeed: '',
    cekB64: '',
  },
};

const urlObject = {
  url: '',
  selectedType: 'ftp',
  login: '',
  password: '',
  hostPath: '',
  secure: false, // FTP
  showDetails: false,
};

const inputSupportedSchemes = {
  ftp: true,
  sftp: true,
};

const outputSupportedSchemes = {
  ftp: true,
  sftp: true,
  aspera: true,
  azure: true,
  origin: true,
};

function buildURL(urlObj) {
  let scheme = urlObj.selectedType;
  if (scheme === 'http' && urlObj.secure) {
    scheme = 'https';
  }
  if (scheme === 'ftp' && urlObj.secure) {
    scheme = 'sftp';
  }
  let lp = '';
  if (urlObj.login) {
    lp = urlObj.password ? `${urlObj.login}:${urlObj.password}@` : `${urlObj.login}@`;
  }
  const { hostPath } = urlObj;
  return `${scheme}://${lp}${hostPath}`;
}

function NormOutputToJsonAPI(output) {
  const convertedOutput = {
    format: output.format,
    url: [],
    zip: output.zip,
    map: [],
  };
  if (output.format === 'segmented_hls') {
    convertedOutput.hls_version = Number(output.hlsVersion);
  }
  for (let i = 0; i < output.urls.length; i += 1) {
    convertedOutput.url.push(output.urls[i].url);
  }
  if (output.format.startsWith('segmented')) {
    convertedOutput.segment_size = Number(output.segmentSize);
  }
  if (['segmented_dash', 'segmented_ss'].includes(output.format) && output.drm === 'playready') {
    convertedOutput.drm = 'playready';
    convertedOutput.playready_la_url = output.playready.licenseServerURL;
    convertedOutput.playready_key_id = output.playready.kidUUID;
    convertedOutput.playready_key_seed = output.playready.keySeed;
    convertedOutput.playready_content_key = output.playready.cekB64;
  }

  if (output.format === 'segmented_dash' && output.drm === 'widevine') {
    convertedOutput.drm = 'widevine';
    convertedOutput.widevine_license_server = output.widevine.licenseServer;
    convertedOutput.widevine_provider = output.widevine.provider;
    convertedOutput.widevine_key = output.widevine.key;
    convertedOutput.widevine_iv = output.widevine.iv;
    convertedOutput.widevine_package_name = output.widevine.packageName;
  }
  return convertedOutput;
}

function NormWFToJsonAPI(wf) {
  const t = + new Date();
  const converted = {
    ext_id: `${t}:${wf.name}`,
    name: wf.name,
    url: wf.url.url,
    outputs: [],
    profile: {
      id: Number(wf.profile),
    },
    once: wf.once,
  };
  for (let i = 0; i < wf.outputs.length; i += 1) {
    converted.outputs.push(NormOutputToJsonAPI(wf.outputs[i]));
  }
  return converted;
}

export default {
  created() {
    this.form.url = Vue.util.extend({}, urlObject);

    const output = Vue.util.extend({}, outputObject);
    output.urls = [];
    const urlObj = Vue.util.extend({}, urlObject);
    urlObj.selectedType = 'ftp';
    output.urls.push(urlObj);
    this.form.outputs.push(output);

    this.$store.commit('setPageLoader');
    this.$store.dispatch('encodingProfiles/getProfiles')
      .then((profiles) => {
        this.profiles = profiles;
      })
      .catch((errorMsg) => {
        this.$toasted.error(errorMsg, {
          theme: 'toasted-primary',
          position: 'top-center',
          duration: 10000,
        });
      })
      .finally(() => this.$store.commit('resolvePageLoader'));
  },
  data() {
    return {
      profileInfoDialog: false,
      isEditing: false,
      isFormValid: false,
      isSaving: false,
      currentTab: 'general',
      formatInfoDialog: false,
      form: {
        name: '',
        profile: '',
        urgentTask: false,
        url: {}, // urlObject
        outputs: [], // array of outputObject
        once: false,
      },
      rules: {
        nameRules: [
          v => !!v || 'Name is required',
          v => (v.length >= 3 && v.length <= 255) || 'Name must be between 3 and 255 characters',
        ],
        segmentSize: [
          v => (v >= 1 && v <= 60) || 'Must be between 1 and 60',
        ],
        drmServerURL: [
          v => (!v || v.startsWith('https://')) || '',
        ],
        wvProvider: [
          v => (!v || (v.length >= 1 && v.length <= 512)) || 'Invalid provider',
        ],
        wvKey: [
          v => (!v || v.length === 64) || '',
        ],
        wvIV: [
          v => (!v || v.length === 32) || '',
        ],
        wvPackageName: [
          v => (!v || (v.length >= 3 && v.length <= 128)) || 'Package name must be between 3 and 128 characters',
        ],
        prKeyID: [
          v => (!v || v.length === 36) || '',
        ],
        prKeySeed: [
          v => (!v || v.length > 1) || '',
        ],
        prCekB64: [
          v => (!v || v.length === 24) || '',
        ],
      },
      formats: ENCODING_FORMATS,
      profiles: [],
      hlsVersions: [3, 5],
      videoCodecMap: VIDEO_CODEC_MAP,
      audioChannelsMap: AUDIO_CHANNELS_MAP,
      h264ProfileMap: H264_CODEC_PROFILE_MAP,
      h265ProfileMap: H265_CODEC_PROFILE_MAP,
      resizeModeMap: RESIZE_MODE_MAP,
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    isWFValid() {
      if (!(this.form.name.length >= 3 && this.form.name.length <= 255) || this.form.profile === '') {
        return false;
      }
      if (this.form.url.hostPath === '') return false;
      let noOutput = true;
      for (let i = 0; i < this.form.outputs.length; i += 1) {
        const output = this.form.outputs[i];
        if (output.format.startsWith('segmented') && !(output.segmentSize >= 1 && output.segmentSize <= 60)) {
          return false;
        }
        for (let j = 0; j < output.urls.length; j += 1) {
          const url = output.urls[j];
          if (url.url !== '' && url.selectedType !== 'origin' && url.hostPath === '' ) {
            return false;
          }
          if (url.url !== '') {
            noOutput = false;
          }
        }
      }
      return !noOutput;
    },
    selectedProfile() {
      for (let i = 0; i < this.profiles.length; i += 1) {
        if (this.profiles[i].id === this.form.profile) {
          return this.profiles[i];
        }
      }
      return {};
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    buildInputURL() {
      this.form.url.url = buildURL(this.form.url);
    },
    onInputURLChange() {
      this.form.url = Vue.util.extend({}, processURLInfo(this.form.url, inputSupportedSchemes, 'ftp'));
    },
    changeInputSecure() {
      this.form.url.secure = !this.form.url.secure;
      this.buildInputURL();
    },
    outputLabel(i, j) {
      if (this.form.outputs[i].urls.length === 1) {
        return 'Destination *';
      }
      const order = j + 1;
      return `Destination #${order}`;
    },
    addOutput() {
      const output = Vue.util.extend({}, outputObject);
      output.urls = [];
      const urlObj = Vue.util.extend({}, urlObject);
      urlObj.selectedType = 'ftp';
      output.urls.push(urlObj);
      this.form.outputs.push(Vue.util.extend({}, output));
    },
    removeOutput(i) {
      this.form.outputs.splice(i, 1);
    },
    onFormatChange(i) {
      this.setDRM(i, 'none');
    },
    buildOutputUrl(i, j) {
      this.form.outputs[i].urls[j].url = buildURL(this.form.outputs[i].urls[j]);
    },
    onOutputURLChange(i, j) {
      const url = processURLInfo(this.form.outputs[i].urls[j], outputSupportedSchemes, 'ftp');
      Vue.set(this.form.outputs[i].urls, j, url);
    },
    changeOutputType(i, j, type) {
      this.form.outputs[i].urls[j].selectedType = type;
      this.buildOutputUrl(i, j);
    },
    changeOutputSecure(i, j) {
      this.form.outputs[i].urls[j].secure = !this.form.outputs[i].urls[j].secure;
      this.buildOutputUrl(i, j);
    },
    addOutputURL(i) {
      const urlObj = Vue.util.extend({}, urlObject);
      urlObj.selectedType = 'ftp';
      this.form.outputs[i].urls.push(urlObj);
    },
    removeOutputURL(i, j) {
      this.form.outputs[i].urls.splice(j, 1);
    },
    setDRM(i, type) {
      this.form.outputs[i].drm = type;
    },
    saveWF() {
      this.isSaving = true;
      if (!this.isWFValid) return;

      const wf = NormWFToJsonAPI(this.form);

      this.$store.dispatch('watchFolders/createWatchFolder', wf)
        .then((result) => {
          this.$router.push({
            name: 'WatchFolderDetailPage',
            params: {
              id: result.id,
            },
          });
        })
        .catch((errorMsg) => {
          this.isSaving = false;
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
